import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { CreateDashboardItemDTO, CreateGrafanaTableDTO, CreateResponseDTO, DashboardGraph, DashboardItemType, FindItem, UpdateDashboardItemDTO } from "@dto/dashboard.dto";
import { MenuDataNode } from "@parts/dashboard/select/DashboardContextMenu";
import { deleteApi, postApi, putApi } from "src/util/apiCalls";
import { API_URL } from "../../data/Api";
import { Log } from '../../service/log.service';
import AbstractDashboardSelect, { DashboardSelect, DashboardSelectProps } from "../dashboard/select/AbstractDashboardSelect";

interface TableDashboardSelectProps extends DashboardSelectProps {
  id: string;
  type: DashboardItemType;
  /** User group the alert is shared with, if any. */
  shareGroupId?: number;
}

export default class TableDashboardSelect extends AbstractDashboardSelect<TableDashboardSelectProps> {

  protected get itemLabel() { return 'table'; }

  /** An alert reaches a shared dashboard only for the group it is itself shared with. */
  protected groupPlacementDenial(groupId: number): string {
    return this.props.type !== 'alerting_table' || this.props.shareGroupId === groupId
      ? undefined
      : 'The alert is not shared with this group.';
  }

  protected updateItem(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode, dashboardItems: FindItem[], path: string) {
    const found = dashboardItems.find(fg => fg.dashboard_item_id === ds.id);
    if (!found) {
      const msg = 'Update table in dashboard failed, ID lost: ' + ds.id;
      Log.error(msg);
      return;
    }
    found.dashboard_item_id = ds.id;
    found.dashboard_group = { dashboard_group_id: group.itemId, label: group.titleText };
    found.dashboard = { dashboard_id: dash.itemId, label: dash.titleText };
    found.path = path;
  }

  protected addItem(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode, dashboardItems: FindItem[], path: string) {
    dashboardItems.push({
      dashboard_item_id: ds.id || String(ds.selectId),
      dashboard_group: { dashboard_group_id: group.itemId, label: group.titleText },
      dashboard: { dashboard_id: dash.itemId, label: dash.titleText },
      path
    });
  }

  protected showItemInDashboardWarn(dash: MenuDataNode, ds: DashboardSelect) {
    if (this.state.dashboardItems.some(fg => !this.isOwnItem(ds, fg) && fg.dashboard.dashboard_id === dash.itemId)) {
      Log.warn('Table is already present in this dashboard location.');
      return true;
    }
  }

  protected postToGrafana(dashboard: MenuDataNode) {
    const c = this.props.common;
    return postApi<BasicResponseDTO, CreateGrafanaTableDTO>(API_URL.DASHBOARD_GRAFANA + '/table',
      {
        grafana_dashboard_uid: dashboard.itemId.replace(AbstractDashboardSelect.GRAFANA_PREFIX, ''),
        tab: c.tabName,
        subtab: c.subTabName,
        url: c.dashUrl,
        id: this.props.id,
        tabId: c.tabId
      }).then(response => {
        //this.addGrafana(ds, dash);
      }, reason => Log.error('Failed to add table to Grafana dashboard ' + dashboard.titleText, reason));
  }

  protected postToDashboard(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode): Promise<unknown> {
    const c = this.props.common;
    return postApi<CreateResponseDTO, CreateDashboardItemDTO>(API_URL.DASHBOARD_ITEM,
      {
        dashboard_group_id: group.itemId,
        tab: c.tabName,
        subtab: c.subTabName,
        url: c.dashUrl,
        id: this.props.id,
        tabId: c.tabId,
        type: this.props.type
      }).then(response => {
        ds.id = response.data.data.id;
        this.addItem(ds, dash, group, this.props.common.dashboardItems, ds.value);
      }, reason => Log.error('Failed to add table to dashboard group ' + group.titleText, reason));
  }

  protected updateInDashboard(ds: DashboardSelect, dash: MenuDataNode, group: MenuDataNode): Promise<unknown> {
    if (this.props.type === 'alerting_table') {
      // the server has no move for a single alert, it would keep the original one - drop it and add it to the target group
      return deleteApi<BasicResponseDTO>(API_URL.DASHBOARD_ITEM + '/' + ds.id).then(() => {
        this.removeItem(ds, this.props.common.dashboardItems);
        return this.postToDashboard(ds, dash, group);
      }, reason => Log.error('Failed to remove alert from group ' + group.titleText, reason));
    }

    return putApi<BasicResponseDTO, UpdateDashboardItemDTO>(API_URL.DASHBOARD_ITEM + '/' + ds.id,
      { dashboard_group_id: group.itemId, x: DashboardGraph.DEFAULT_COORDINATE, y: DashboardGraph.DEFAULT_COORDINATE }).then(response => {
        this.updateItem(ds, dash, group, this.props.common.dashboardItems, ds.value);
      }, reason => Log.error('Failed to update table in group ' + group.titleText, reason));
  }

  protected addGrafana(ds: DashboardSelect, dash: MenuDataNode) {
    this.state.grafanaItems.push({
      dashboard: { grafana_dashboard_uid: dash.itemId, label: dash.titleText },
      grafana_item_uid: ds.id,
      id: this.props.id
    });
  }
}